// frontend/src/pages/Survey.js

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { surveyApi } from '../services/api';
import './Survey.css';
import zaytoonLogo from '../images/zaytoon_logo_black.png';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMicrophone, faStop, faAngleRight } from '@fortawesome/free-solid-svg-icons';

function Survey() {
  const { projectId, customerId: urlCustomerId } = useParams();
  const [searchParams] = useSearchParams();
  const isPreview = searchParams.get('preview') === 'true';
  const [project, setProject] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [responses, setResponses] = useState([]);
  const [answer, setAnswer] = useState('');
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isComplete, setIsComplete] = useState(false);
  const [surveyStarted, setSurveyStarted] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [answeredQuestions, setAnsweredQuestions] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const mediaRecorderRef = useRef(null);
  const [questions, setQuestions] = useState([]);
  const [customerId, setCustomerId] = useState(null);
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [availableLanguages, setAvailableLanguages] = useState(['en']);
  const [translations, setTranslations] = useState({});
  const [brandColor, setBrandColor] = useState('#1976D2');
  const [logoUrl, setLogoUrl] = useState(zaytoonLogo);
  const [selectedRating, setSelectedRating] = useState(null);
  const [hasAudioPermission, setHasAudioPermission] = useState(null);
  const [audioError, setAudioError] = useState(null);

  const fetchBrandingSettings = useCallback(async () => {
    try {
      const response = await surveyApi.get(`/survey/${projectId}/branding`);
      if (response.data.brand_color) {
        setBrandColor(response.data.brand_color);
        // Convert hex to RGB for opacity support
        const hex = response.data.brand_color.replace('#', '');
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        document.documentElement.style.setProperty('--brand-color-rgb', `${r}, ${g}, ${b}`);
      }
      if (response.data.logo_url) {
        setLogoUrl(response.data.logo_url);
      }
    } catch (error) {
      // Keep default values if fetch fails
    }
  }, [projectId]);

  useEffect(() => {
    setIsLoading(true);
    Promise.all([
      surveyApi.get(`/survey/${projectId}`),
      surveyApi.get(`/survey/${projectId}/translations`),
      fetchBrandingSettings()
    ])
      .then(([surveyResponse, translationsResponse]) => {
        if (surveyResponse.data && surveyResponse.data.questions) {
          setProject(surveyResponse.data);
          setQuestions(surveyResponse.data.questions);
          setTotalQuestions(surveyResponse.data.questions.length);
          setTranslations(translationsResponse.data);
          setAvailableLanguages(Object.keys(translationsResponse.data));

          // Update meta tags with welcome content
          const welcomeHeading = surveyResponse.data.welcome_heading || '';
          const welcomeMessage = surveyResponse.data.welcome_message || '';
          updateMetaTags(welcomeHeading, welcomeMessage, logoUrl);

          setIsLoading(false);
        } else {
          setError('Invalid survey data received.');
          setIsLoading(false);
        }
      })
      .catch(() => {
        setError('Failed to load survey.');
        setIsLoading(false);
      });
  }, [projectId, fetchBrandingSettings]);

  useEffect(() => {
    const timestamp = new Date().toISOString().replace(/[-:.]/g, '');
    const randomId = Math.random().toString(36).substring(2, 10);
    
    if (!urlCustomerId || urlCustomerId === 'anonymous') {
      setCustomerId(`anon_${timestamp}_${randomId}`);
    } else {
      setCustomerId(`${urlCustomerId}_${timestamp}_${randomId}`);
    }
  }, [urlCustomerId]);

  useEffect(() => {
    // Track visit when the page loads
    surveyApi.post(`/survey/${projectId}/visit`);
  }, [projectId]);

  const fetchNextQuestion = (langCode) => {
    setIsLoading(true);
    
    const finalAnswer = currentQuestion?.question_type === 'rating scale' 
      ? selectedRating?.toString() 
      : answer.trim();
    
    const updatedTranscript = transcript + (currentQuestion 
      ? `Q: ${currentQuestion.text}\nA: ${finalAnswer}\n\n` 
      : '');
    setTranscript(updatedTranscript);

    const currentIndex = questions.findIndex(q => q.id === (currentQuestion?.id));
    const nextIndex = currentIndex + 1;
    
    if (nextIndex >= questions.length) {
      completeSurvey(responses, updatedTranscript);
      return;
    }

    const nextQuestion = questions[nextIndex];
    
    surveyApi.post(`/survey/${projectId}/next-question`, {
      customer_id: customerId,
      transcript: updatedTranscript,
      language: langCode || selectedLanguage,
      question_type: nextQuestion.question_type,
      max_scale: nextQuestion.max_scale
    })
      .then(response => {
        if (response.data.question === "SURVEY_COMPLETE") {
          completeSurvey(responses, updatedTranscript);
        } else {
          setCurrentQuestion({
            ...nextQuestion,  // Keep original properties
            text: response.data.question  // Use AI-generated text
          });
          setIsLoading(false);
        }
      })
      .catch(() => {
        // console.error(error);
        setError('Failed to load next question.');
        setIsLoading(false);
      });
  };

  const handleLanguageSelect = (langCode) => {
    setSelectedLanguage(langCode);
    setSurveyStarted(true);
    
    // Track survey start
    surveyApi.post(`/survey/${projectId}/start`, {
      customer_id: customerId
    })
    .then(() => {
      // Always get AI-generated first question
      fetchNextQuestion(langCode);
    })
    .catch(error => {
      console.error('Error starting survey:', error);
      setError('Failed to start survey. Please try again.');
    });
  };

  const handleSubmit = useCallback(() => {
    // For rating scale questions, use selectedRating instead of answer
    const finalAnswer = currentQuestion?.question_type === 'rating scale' 
      ? selectedRating?.toString() 
      : answer.trim();

    // Check for empty answers
    if (!finalAnswer) {
      alert('Please provide an answer before continuing.');
      return;
    }

    const newResponse = {
      question: currentQuestion,
      answer: finalAnswer
    };

    if (isPreview) {
      const updatedResponses = [...responses, newResponse];
      setResponses(updatedResponses);
      setAnswer('');
      setSelectedRating(null);
      setAnsweredQuestions(prevCount => prevCount + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      fetchNextQuestion();
      return;
    }

    setIsLoading(true);
    surveyApi.post(`/survey/${projectId}/answer`, {
      customer_id: customerId,
      question: currentQuestion.text,
      answer: finalAnswer,
      transcript: transcript
    })
      .then(() => {
        const updatedResponses = [...responses, newResponse];
        setResponses(updatedResponses);
        setAnswer('');
        setSelectedRating(null);
        setAudioBlob(null);
        setIsLoading(false);
        setAnsweredQuestions(prevCount => prevCount + 1);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        fetchNextQuestion();
      })
      .catch(error => {
        setError('Failed to save answer.');
        setIsLoading(false);
      });
  }, [
    currentQuestion,
    answer,
    selectedRating,
    isPreview,
    responses,
    customerId,
    projectId,
    transcript
  ]);

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleSubmit();
    }
  };

  const completeSurvey = (responses, finalTranscript) => {
    setIsLoading(true);
    surveyApi.post(`/survey/${projectId}/complete`, {
      customer_id: customerId,
      responses: responses,
      transcript: finalTranscript,
      is_analyzed: true
    })
      .then(() => {
        setIsComplete(true);
        setIsLoading(false);
      })
      .catch(error => {
        console.error('Error completing survey:', error);
        setError('Failed to complete survey. Please try again.');
        setIsLoading(false);
      });
  };

  const startRecording = async () => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setAudioError('Audio recording is not supported on this device/browser.');
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100,  // Specify standard sample rate
          channelCount: 1     // Mono recording
        } 
      });

      // Create the MediaRecorder instance with Safari-compatible options
      let options = {};
      
      // Try different MIME types for better Safari compatibility
      const mimeTypes = [
        'audio/mp4',
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/ogg;codecs=opus',
        ''  // Empty string lets browser choose format
      ];

      for (const mimeType of mimeTypes) {
        try {
          options = mimeType ? { mimeType } : {};
          mediaRecorderRef.current = new MediaRecorder(stream, options);
          break;
        } catch (e) {
          continue;
        }
      }

      if (!mediaRecorderRef.current) {
        throw new Error('Could not create MediaRecorder with any supported format');
      }

      // Set up the data handling
      const chunks = [];
      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
        }
      };

      mediaRecorderRef.current.onstop = () => {
        let blobOptions = {
          type: mediaRecorderRef.current.mimeType || 'audio/webm'
        };
        
        // Special handling for Safari
        if (/^((?!chrome|android).)*safari/i.test(navigator.userAgent)) {
          blobOptions.type = 'audio/mp4';
        }
        
        const blob = new Blob(chunks, blobOptions);
        setAudioBlob(blob);
        
        // Stop all tracks to release microphone
        stream.getTracks().forEach(track => track.stop());
      };

      // Start recording with smaller timeslices for Safari (1 second intervals)
      mediaRecorderRef.current.start(1000);
      setIsRecording(true);
      setHasAudioPermission(true);
      setAudioError(null);

    } catch (error) {
      console.error('Microphone error:', error);
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        setAudioError('Please allow microphone access to record audio.');
        setHasAudioPermission(false);
      } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
        setAudioError('No microphone found on this device.');
        setHasAudioPermission(false);
      } else {
        setAudioError('Error accessing microphone. Please try again.');
        setHasAudioPermission(false);
      }
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const handleTranscription = useCallback(async () => {
    if (!audioBlob) return;

    // Map language codes to ISO-639-1 format for Whisper API
    const languageMap = {
      'en': 'en',  // English
      'kz': 'kk',  // Kazakh
      'ru': 'ru'   // Russian
    };

    // Create a new blob with the correct MIME type for Safari
    let formattedBlob;
    if (/^((?!chrome|android).)*safari/i.test(navigator.userAgent)) {
      // For Safari, convert to mp4
      formattedBlob = new Blob([audioBlob], { type: 'audio/mp4' });
    } else {
      // For other browsers, use webm
      formattedBlob = new Blob([audioBlob], { type: 'audio/webm' });
    }

    const formData = new FormData();
    // Use .mp4 extension for Safari, .webm for others
    const fileExtension = /^((?!chrome|android).)*safari/i.test(navigator.userAgent) ? 'mp4' : 'webm';
    formData.append('audio', formattedBlob, `recording.${fileExtension}`);
    formData.append('language', languageMap[selectedLanguage] || selectedLanguage);
    
    if (currentQuestion) {
      formData.append('prompt', currentQuestion);
    }

    try {
      setIsLoading(true);
      const response = await surveyApi.post(`/survey/${projectId}/transcribe`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setAnswer(response.data.transcription);
    } catch (error) {
      console.error('Error transcribing audio:', error);
      setError('Failed to transcribe audio.');
    } finally {
      setIsLoading(false);
    }
  }, [audioBlob, projectId, selectedLanguage, currentQuestion]);

  useEffect(() => {
    if (audioBlob) {
      handleTranscription();
    }
  }, [audioBlob, handleTranscription]);

  useEffect(() => {
    if (selectedRating !== null && currentQuestion?.question_type === 'rating scale') {
      handleSubmit();
    }
  }, [selectedRating, currentQuestion?.question_type, handleSubmit]);

  const updateMetaTags = (welcomeHeading, welcomeMessage, logoUrl) => {
    // Update title and description
    document.title = welcomeHeading || 'Zaytoon Survey';
    
    // Helper function to update meta tags
    const updateMetaTag = (selector, content) => {
      let tag = document.querySelector(selector);
      if (tag) {
        tag.setAttribute('content', content);
      }
    };

    // Update standard meta description
    updateMetaTag(
      'meta[name="description"]',
      `${welcomeHeading} - ${welcomeMessage}`
    );

    // Update OpenGraph meta tags
    updateMetaTag('meta[property="og:title"]', welcomeHeading);
    updateMetaTag('meta[property="og:description"]', welcomeMessage);
    updateMetaTag('meta[property="og:image"]', logoUrl);

    // Update Twitter Card meta tags
    updateMetaTag('meta[name="twitter:title"]', welcomeHeading);
    updateMetaTag('meta[name="twitter:description"]', welcomeMessage);
    updateMetaTag('meta[name="twitter:image"]', logoUrl);
  };

  if (error) return <div className="loading"></div>;
  if (!project) return <div className="loading"></div>;
  if (!customerId) return <div className="loading"></div>;

  const progressPercentage = totalQuestions > 0 
    ? Math.min((answeredQuestions / totalQuestions) * 100, 100) 
    : 0;

  return (
    <div className="survey-container" style={{'--brand-color': brandColor}}>
      <div className="progress-bar-container">
        <div className="progress-bar" style={{ width: `${progressPercentage}%` }}></div>
      </div>
      <div className="survey-content">
        {!surveyStarted ? (
          <div className="language-selection">
            <div className="language-buttons">
              {availableLanguages.map(langCode => (
                <button
                  key={langCode}
                  className="language-button"
                  onClick={() => handleLanguageSelect(langCode)}
                >
                  <span className="language-code" style={{ color: brandColor }}>
                    {langCode.toUpperCase()}
                  </span>
                  <div className="language-content">
                    <div className="language-heading">
                      {translations[langCode]?.welcome_heading || project.welcome_heading}
                    </div>
                    <div className="language-message">
                      {translations[langCode]?.welcome_message || project.welcome_message}
                    </div>
                  </div>
                  <span className="language-arrow">›</span>
                </button>
              ))}
            </div>
          </div>
        ) : isComplete ? (
          <>
            <h2 className="question-text">
              {(translations[selectedLanguage]?.goodbye_message || project.goodbye_message || "Thank you! Your responses have been recorded.")}
            </h2>
          </>
        ) : (
          <>
            <h2 className="question-text">{currentQuestion?.text}</h2>
            <div className="answer-input-container">
              {currentQuestion && currentQuestion.question_type === 'rating scale' ? (
                <div className="rating-scale-container">
                  {[...Array(parseInt(currentQuestion.max_scale) || 5)].map((_, index) => (
                    <button
                      key={index + 1}
                      onClick={() => setSelectedRating(index + 1)}
                      className="rating-button"
                    >
                      {index + 1}
                    </button>
                  ))}
                </div>
              ) : (
                <>
                  <input
                    type="text"
                    value={answer}
                    onChange={(e) => setAnswer(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Text"
                    className="answer-input"
                  />
                  <button
                    onClick={isRecording ? stopRecording : startRecording}
                    className={`record-button ${isRecording ? 'recording' : ''}`}
                    type="button"
                    disabled={audioError && !hasAudioPermission}
                    title={audioError || 'Record audio'}
                  >
                    <FontAwesomeIcon icon={isRecording ? faStop : faMicrophone} />
                  </button>
                  <button onClick={handleSubmit} className="submit-button" disabled={isLoading}>
                    {isLoading ? (
                      <div className="loading-circle"></div>
                    ) : (
                      <FontAwesomeIcon icon={faAngleRight} />
                    )}
                  </button>
                </>
              )}
            </div>
          </>
        )}
      </div>
      <div className="survey-footer">
        <img src={logoUrl} alt="Brand Logo" className="branding" />
        <div className="powered-by">
          Powered by <a href="https://zaytoon.io" target="_blank" rel="noopener noreferrer">zaytoon.io</a>
        </div>
      </div>
      {audioError && (
        <div className="audio-error-message">
          {audioError}
        </div>
      )}
    </div>
  );
}

export default Survey;
